import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FileText, Briefcase, TrendingUp, BookOpen, ArrowRight, Mic, MicOff, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareerAdvisor() {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [audioSrc, setAudioSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    zip_code: '',
    target_job_title: '',
    radius: '25',
    work_mode: 'all',
    salary_min: '80000',
    salary_max: '300000',
    consent: false,
  });
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx', 'doc', 'txt'].includes(ext)) {
        setError('Please upload a PDF, DOCX, or TXT resume.');
        return;
      }
      setFile(f);
      setError('');
      setAnalysis(null);
      setAudioSrc(null);
    }
  };

  const formValid = () =>
    form.full_name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.address.trim() &&
    form.target_job_title.trim() &&
    form.consent;

  const handleAnalyze = async () => {
    if (!file) { setError('Please attach your resume (PDF, DOCX, or TXT).'); return; }
    if (!formValid()) { setError('Please complete the required form fields and check the consent box.'); return; }
    setAnalyzing(true);
    setError('');
    setAnalysis(null);
    setAudioSrc(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.entries(form).forEach(([k, v]) => formData.append(k, String(v)));
      const { data } = await axios.post(`${API}/career-advisor/analyze`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });

      setAnalysis(data.analysis);
      // Persist the top match scores for JobBoard to pick up
      if (data.analysis?.job_matches || data.analysis?.matched_keywords) {
        try { sessionStorage.setItem('ioa_resume_match', JSON.stringify({
          email: form.email,
          target: form.target_job_title,
          matches: data.analysis?.job_matches || [],
          ts: Date.now(),
        })); } catch {}
      }

      if (data.audio_base64) {
        setAudioSrc(`data:audio/mp3;base64,${data.audio_base64}`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 504 || e.code === 'ECONNABORTED') {
        setError('The AI service is temporarily busy. Please wait a moment and try again.');
      } else if (status === 429) {
        setError(detail || 'You have already submitted a resume recently. Please try again in 24 hours.');
      } else {
        setError(detail || 'Analysis failed. Please try again.');
      }
    }
    setAnalyzing(false);
  };

  useEffect(() => {
    if (audioSrc && audioRef.current) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        setIsSpeaking(true);
      }).catch(() => {});
    }
  }, [audioSrc]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsSpeaking(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      setIsSpeaking(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="AI Career Advisor — Free Resume Analysis"
        description="Upload your resume for free AI-powered analysis: ATS scoring, recruiter outlook, emotional approach, AI-generated detection, and personalized job matches. Voice advice from Dr. Nethra."
        path="/career-advisor"
      />
      {/* Hero */}
      <section className="py-20 px-6 bg-[#111111] border-b border-[#262626]">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className={`w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden border-4 transition-all duration-300 ${isSpeaking ? 'border-[#0077B6] shadow-[0_0_30px_rgba(0,119,182,0.4)]' : 'border-[#262626]'}`}>
              <img
                src="/dr_nethra_avatar.jpg"
                alt="Dr. Nethra Sambamoorthi"
                className="w-full h-full object-cover"
                data-testid="advisor-avatar"
              />
            </div>
            {isSpeaking && (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1 items-end">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="w-1 bg-[#0077B6] animate-pulse" style={{ height: `${8 + Math.random() * 16}px`, animationDelay: `${i * 0.1}s`, animationDuration: '0.5s' }} />
                ))}
              </div>
            )}
          </div>

          {/* Intro */}
          <div className="flex-1 text-center lg:text-left">
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>AI Career Advisor</p>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Meet <span className="text-[#0077B6]">Dr. Nethra Sambamoorthi</span>
            </h1>
            <p className="text-base text-zinc-300 max-w-xl mb-6 leading-relaxed">
              Founder & Professor at Institute of Analytics USA. Upload your resume and get personalized AI career advice — job matches, skill gap analysis, and upskilling recommendations.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#F59E0B]" /> Resume Analysis</span>
              <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-[#F59E0B]" /> Job Matching</span>
              <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-[#F59E0B]" /> Skill Gap Analysis</span>
              <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[#F59E0B]" /> Upskilling Path</span>
            </div>
          </div>
        </div>
      </section>

      {/* Upload Section */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {!analysis ? (
            <div className="max-w-3xl mx-auto">
              {/* Yellow info banner */}
              <div className="mb-8 pl-4 py-3 border-l-4 border-[#F59E0B] bg-[#F59E0B]/5 text-sm text-zinc-200" data-testid="career-form-banner">
                <p className="font-semibold text-[#F59E0B] mb-0.5">Resume analysis — a brief form precedes the upload</p>
                <p className="text-zinc-400 text-xs">The information below powers a personalized AI evaluation (resume scoring, job-fit %, salary alignment) and enables us to notify you with matched openings.</p>
              </div>

              {/* The multi-field form */}
              <div className="bg-[#0D1F2D] border border-[#0077B6]/40 p-6 sm:p-8 space-y-6 mb-6" data-testid="career-form">
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Tell us about you</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Full Name *</label>
                    <input type="text" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-fullname" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Email *</label>
                    <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Phone *</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(555) 123-4567" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">ZIP Code *</label>
                    <input type="text" value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} placeholder="10001" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-zip" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Address *</label>
                    <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="City, State" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-address" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Target Job Title *</label>
                    <input type="text" value={form.target_job_title} onChange={e => setForm({...form, target_job_title: e.target.value})} placeholder="e.g., Data Scientist, ML Engineer, AI Product Lead" className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-target-title" />
                  </div>
                </div>

                <div className="pt-4 border-t border-[#262626]">
                  <p className="text-xs font-bold tracking-widest uppercase text-[#F59E0B] mb-4">Job Matching Filters</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Radius from ZIP</label>
                      <select value={form.radius} onChange={e => setForm({...form, radius: e.target.value})} className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-radius">
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                        <option value="100">100 miles</option>
                        <option value="national">Anywhere (national)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Work Mode</label>
                      <select value={form.work_mode} onChange={e => setForm({...form, work_mode: e.target.value})} className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="form-workmode">
                        <option value="all">All</option>
                        <option value="remote">Remote</option>
                        <option value="hybrid">Hybrid</option>
                        <option value="onsite">On-site</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold tracking-widest uppercase text-[#F59E0B] mb-1.5">Salary Range (USD)</label>
                      <div className="flex gap-2 items-center">
                        <input type="number" value={form.salary_min} onChange={e => setForm({...form, salary_min: e.target.value})} step="5000" min="0" className="w-1/2 px-2 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6] text-sm" data-testid="form-salary-min" />
                        <span className="text-zinc-500">–</span>
                        <input type="number" value={form.salary_max} onChange={e => setForm({...form, salary_max: e.target.value})} step="5000" min="0" className="w-1/2 px-2 py-2.5 bg-[#0A0A0A] border border-[#262626] text-white outline-none focus:border-[#0077B6] text-sm" data-testid="form-salary-max" />
                      </div>
                    </div>
                  </div>
                </div>

                <label className="flex items-start gap-3 pt-2 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={e => setForm({...form, consent: e.target.checked})} className="mt-1 w-4 h-4 accent-[#F59E0B]" data-testid="form-consent" />
                  <span className="text-xs text-zinc-300">I consent to Institute of Analytics USA storing my resume and contact information for the purpose of resume analysis and job-match notifications. I understand I can request deletion at any time by emailing nsm@instituteofanalytics.com.</span>
                </label>
              </div>

              {/* Upload Zone */}
              <div
                className="border-2 border-dashed border-[#262626] bg-[#111111] p-10 text-center hover:border-[#0077B6] transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="resume-upload-zone"
              >
                <Upload className="w-14 h-14 text-[#0077B6] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-lg text-white font-semibold mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {file ? file.name : 'Drop your resume here or click to upload'}
                </p>
                <p className="text-sm text-zinc-400">PDF, DOCX, or TXT (max 10MB)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="resume-file-input"
                />
              </div>

              {file && (
                <div className="mt-6 flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#262626]">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-[#0077B6] flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-white truncate">{file.name}</span>
                    <span className="text-xs text-zinc-400 flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button
                    onClick={handleAnalyze}
                    disabled={analyzing || !formValid()}
                    className="brand-btn text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-50 flex-shrink-0"
                    data-testid="analyze-resume-btn"
                  >
                    {analyzing ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                    ) : (
                      <>Analyze My Resume <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>
                    )}
                  </button>
                </div>
              )}

              {!formValid() && file && (
                <p className="mt-3 text-xs text-[#F59E0B]" data-testid="form-incomplete-hint">Please complete all required fields and check the consent box above to continue.</p>
              )}

              {analyzing && (
                <div className="mt-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-[#0077B6] animate-pulse">
                    <img src="/dr_nethra_avatar.jpg" alt="" className="w-full h-full object-cover" />
                  </div>
                  <p className="text-base text-[#0077B6] font-semibold mb-2">Dr. Nethra is reviewing your resume...</p>
                  <p className="text-sm text-zinc-400">This may take 15-30 seconds</p>
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] text-sm flex items-center justify-between" data-testid="advisor-error">
                  <span>{error}</span>
                  {file && <button onClick={handleAnalyze} className="ml-4 px-4 py-1.5 bg-[#CC0000] text-white text-xs font-bold hover:bg-[#CC0000]/80 flex-shrink-0">Retry</button>}
                </div>
              )}
            </div>
          ) : (
            /* Results */
            <div>
              {/* Audio player (hidden) */}
              {audioSrc && (
                <audio
                  ref={audioRef}
                  src={audioSrc}
                  onEnded={() => { setIsPlaying(false); setIsSpeaking(false); }}
                  onPause={() => { setIsPlaying(false); setIsSpeaking(false); }}
                  onPlay={() => { setIsPlaying(true); setIsSpeaking(true); }}
                />
              )}

              {/* Header with avatar and audio control */}
              <div className="flex items-center gap-6 mb-10 p-6 bg-[#111111] border border-[#262626]">
                <div className={`w-16 h-16 rounded-full overflow-hidden border-2 flex-shrink-0 transition-all ${isSpeaking ? 'border-[#0077B6] shadow-[0_0_20px_rgba(0,119,182,0.3)]' : 'border-[#262626]'}`}>
                  <img src="/dr_nethra_avatar.jpg" alt="Dr. Nethra" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-lg text-white font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{analysis.greeting || analysis.overall_assessment || "Here are my recommendations."}</p>
                  {analysis.final_verdict && <p className="text-sm text-[#0077B6] mt-1 font-medium">{analysis.final_verdict}</p>}
                </div>
                {audioSrc && (
                  <button onClick={toggleAudio} className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white transition-colors" data-testid="play-audio-btn">
                    {isPlaying ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                )}
              </div>

              {/* Overall Score */}
              {analysis.overall_score !== undefined && (
                <div className="flex items-center gap-6 mb-8 p-6 bg-[#0A0A0A] border border-[#262626]" data-testid="score-section">
                  <div className={`w-24 h-24 flex items-center justify-center border-4 flex-shrink-0 ${
                    analysis.overall_score >= 85 ? 'border-green-500' :
                    analysis.overall_score >= 65 ? 'border-[#0077B6]' :
                    analysis.overall_score >= 45 ? 'border-yellow-500' :
                    'border-[#CC0000]'
                  }`}>
                    <div className="text-center">
                      <p className={`text-3xl font-black ${
                        analysis.overall_score >= 85 ? 'text-green-400' :
                        analysis.overall_score >= 65 ? 'text-[#0077B6]' :
                        analysis.overall_score >= 45 ? 'text-yellow-400' :
                        'text-[#F59E0B]'
                      }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{analysis.overall_score}</p>
                      <p className="text-xs text-zinc-400">/ 100</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Grade: {analysis.grade}</p>
                    {analysis.overall_assessment && <p className="text-sm text-zinc-300 mt-2">{analysis.overall_assessment}</p>}
                  </div>
                </div>
              )}

              {/* Score Breakdown */}
              {analysis.score_breakdown && (
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6" data-testid="breakdown-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Score Breakdown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(analysis.score_breakdown).map(([key, val]) => {
                      const max = key.includes('job_match') ? 25 : key.includes('ats') ? 20 : key.includes('impact') ? 20 : key.includes('structure') ? 15 : 10;
                      const pct = (val / max) * 100;
                      return (
                        <div key={key} className="p-3 bg-[#0A0A0A] border border-[#262626]">
                          <div className="flex justify-between mb-1">
                            <p className="text-xs text-zinc-400 capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="text-xs font-mono text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{val}/{max}</p>
                          </div>
                          <div className="w-full h-1.5 bg-[#262626]"><div className={`h-full ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-[#0077B6]' : pct >= 40 ? 'bg-yellow-500' : 'bg-[#CC0000]'}`} style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recruiter Outlook */}
              {analysis.recruiter_screening_outlook && (
                <div className="flex gap-4 mb-6">
                  <div className="flex-1 p-4 bg-[#111111] border border-[#262626] text-center">
                    <p className="text-xs text-zinc-400 mb-1">ATS Pass</p>
                    <p className={`text-lg font-bold ${analysis.recruiter_screening_outlook.ats_pass_likelihood === 'High' ? 'text-green-400' : analysis.recruiter_screening_outlook.ats_pass_likelihood === 'Medium' ? 'text-yellow-400' : 'text-[#F59E0B]'}`}>{analysis.recruiter_screening_outlook.ats_pass_likelihood}</p>
                  </div>
                  <div className="flex-1 p-4 bg-[#111111] border border-[#262626] text-center">
                    <p className="text-xs text-zinc-400 mb-1">Interview Likelihood</p>
                    <p className={`text-lg font-bold ${analysis.recruiter_screening_outlook.interview_likelihood === 'High' ? 'text-green-400' : analysis.recruiter_screening_outlook.interview_likelihood === 'Medium' ? 'text-yellow-400' : 'text-[#F59E0B]'}`}>{analysis.recruiter_screening_outlook.interview_likelihood}</p>
                  </div>
                </div>
              )}

              {/* Analysis Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Strengths */}
                {analysis.top_strengths?.length > 0 && (
                  <div className="border border-[#262626] bg-[#111111] p-6" data-testid="strengths-section">
                    <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Top Strengths</h3>
                    <div className="space-y-2">
                      {analysis.top_strengths.map((s, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <p className="text-sm text-zinc-300">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Also support old format */}
                {!analysis.top_strengths && analysis.strengths?.length > 0 && (
                  <div className="border border-[#262626] bg-[#111111] p-6" data-testid="strengths-section">
                    <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Strengths</h3>
                    <div className="space-y-2">
                      {analysis.strengths.map((s, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <p className="text-sm text-zinc-300">{s}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gaps */}
                {analysis.top_gaps?.length > 0 && (
                  <div className="border border-[#262626] bg-[#111111] p-6" data-testid="skill-gaps-section">
                    <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Critical Gaps</h3>
                    <div className="space-y-2">
                      {analysis.top_gaps.map((g, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <p className="text-sm text-zinc-300">{g}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!analysis.top_gaps && analysis.skill_gaps?.length > 0 && (
                  <div className="border border-[#262626] bg-[#111111] p-6" data-testid="skill-gaps-section">
                    <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Skill Gaps</h3>
                    <div className="space-y-2">
                      {analysis.skill_gaps.map((g, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2} />
                          <p className="text-sm text-zinc-300">{g}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Keywords */}
              {(analysis.matched_keywords?.length > 0 || analysis.missing_keywords?.length > 0) && (
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Keyword Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.matched_keywords?.length > 0 && (
                      <div>
                        <p className="text-xs text-green-400 font-bold mb-2">MATCHED</p>
                        <div className="flex flex-wrap gap-1">{analysis.matched_keywords.map((k, i) => <span key={i} className="text-xs px-2 py-0.5 bg-green-900/20 border border-green-600/30 text-green-400">{k}</span>)}</div>
                      </div>
                    )}
                    {analysis.missing_keywords?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#F59E0B] font-bold mb-2">MISSING</p>
                        <div className="flex flex-wrap gap-1">{analysis.missing_keywords.map((k, i) => <span key={i} className="text-xs px-2 py-0.5 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B]">{k}</span>)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Priority Improvements */}
              {analysis.priority_improvements?.length > 0 && (
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6" data-testid="improvements-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Priority Improvements</h3>
                  <div className="space-y-3">
                    {analysis.priority_improvements.map((p, i) => (
                      <div key={i} className="flex gap-3 p-3 bg-[#0A0A0A] border border-[#262626]">
                        <span className="text-xs font-mono font-bold text-[#F59E0B] flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center border border-[#CC0000]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{p.priority}</span>
                        <div>
                          <p className="text-sm text-white font-medium">{p.issue}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">{p.why_it_matters}</p>
                          <p className="text-xs text-[#0077B6] mt-1">Fix: {p.recommended_fix}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bullet Rewrites */}
              {analysis.bullet_rewrites?.length > 0 && (
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6" data-testid="rewrites-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Bullet Rewrites</h3>
                  <div className="space-y-4">
                    {analysis.bullet_rewrites.map((b, i) => (
                      <div key={i} className="p-4 bg-[#0A0A0A] border border-[#262626]">
                        <p className="text-xs text-[#F59E0B] line-through mb-1">{b.original}</p>
                        <p className="text-sm text-green-400 font-medium mb-1">{b.improved}</p>
                        <p className="text-xs text-zinc-500">{b.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Career Advice */}
              {analysis.career_advice && (
                <div className="border border-[#262626] bg-[#111111] p-6 mb-6" data-testid="career-advice-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Career Path Advice</h3>
                  <p className="text-base text-zinc-300 leading-relaxed">{analysis.career_advice}</p>
                </div>
              )}

              {/* Emotional Approach Required */}
              {(analysis.emotional_approach || analysis.emotional_rating) && (
                <div className="border-2 border-[#F59E0B]/40 bg-[#111111] p-6 mb-6" data-testid="emotional-approach-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Emotional Approach Required</h3>
                  <p className="text-xs text-zinc-400 mb-5">How to make recruiters pick up the phone — get 20+ calls from employers</p>

                  {/* Rating Badge */}
                  {(() => {
                    const rating = analysis.emotional_approach?.rating ?? analysis.emotional_rating ?? '';
                    const hook = analysis.emotional_approach?.emotional_hook ?? analysis.emotional_hook ?? '';
                    const dosMet = analysis.emotional_approach?.dos_met ?? [];
                    const dontsViolated = analysis.emotional_approach?.donts_violated ?? [];
                    const rewrites = analysis.emotional_approach?.rewrite_suggestions ?? [];
                    return (<>
                  <div className="mb-6">
                    <span className={`text-sm font-bold px-4 py-2 inline-block ${
                      rating.includes('Interview-Ready') ? 'bg-green-900/30 border border-green-600 text-green-400' :
                      rating.includes('Good') ? 'bg-yellow-900/30 border border-yellow-600 text-yellow-400' :
                      'bg-[#F59E0B]/20 border border-[#CC0000] text-[#F59E0B]'
                    }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {rating}
                    </span>
                  </div>

                  {hook && (
                    <div className="mb-6 p-4 bg-[#0077B6]/10 border-l-4 border-[#0077B6]">
                      <p className="text-base text-white leading-relaxed italic">{hook}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {dosMet.length > 0 && (
                      <div>
                        <p className="text-xs font-mono font-bold text-green-400 mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>WHAT YOU'RE DOING RIGHT</p>
                        <div className="space-y-2">{dosMet.map((d, i) => (<div key={i} className="flex items-start gap-2"><span className="text-green-400 flex-shrink-0 mt-0.5">+</span><p className="text-sm text-zinc-300">{d}</p></div>))}</div>
                      </div>
                    )}
                    {dontsViolated.length > 0 && (
                      <div>
                        <p className="text-xs font-mono font-bold text-[#F59E0B] mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>WHAT NEEDS TO CHANGE</p>
                        <div className="space-y-2">{dontsViolated.map((d, i) => (<div key={i} className="flex items-start gap-2"><span className="text-[#F59E0B] flex-shrink-0 mt-0.5">-</span><p className="text-sm text-zinc-300">{d}</p></div>))}</div>
                      </div>
                    )}
                  </div>

                  {rewrites.length > 0 && (
                    <div>
                      <p className="text-xs font-mono font-bold text-[#0077B6] mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>REWRITES TO GET 20+ EMPLOYER CALLS</p>
                      <div className="space-y-2">{rewrites.map((s, i) => (<div key={i} className="flex items-start gap-3 p-3 bg-[#0A0A0A] border border-[#262626]"><span className="text-xs font-mono font-bold text-[#F59E0B] flex-shrink-0 mt-0.5">{i+1}.</span><p className="text-sm text-zinc-300">{s}</p></div>))}</div>
                    </div>
                  )}
                    </>);
                  })()}
                </div>
              )}

              {/* AI-Generated Resume Detection */}
              {(analysis.ai_detection || analysis.ai_score !== undefined) && (
                <div className="border-2 border-[#0077B6]/40 bg-[#111111] p-6 mb-8" data-testid="ai-detection-section">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-[0.15em] text-[#0077B6] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>AI-Generated Resume Score</h3>
                  <p className="text-xs text-zinc-400 mb-5">How authentic does your resume sound to recruiters?</p>

                  {(() => {
                    const score = analysis.ai_detection?.ai_score ?? analysis.ai_score ?? 0;
                    const verdict = analysis.ai_detection?.verdict ?? analysis.ai_verdict ?? '';
                    const tells = analysis.ai_detection?.ai_tells ?? analysis.ai_tells ?? [];
                    const tips = analysis.ai_detection?.authenticity_tips ?? [];
                    return (<>
                  <div className="flex items-center gap-6 mb-6">
                    <div className={`w-20 h-20 flex items-center justify-center border-4 flex-shrink-0 ${
                      score <= 20 ? 'border-green-500 text-green-400' :
                      score <= 40 ? 'border-green-600 text-green-400' :
                      score <= 60 ? 'border-yellow-500 text-yellow-400' :
                      score <= 80 ? 'border-orange-500 text-orange-400' :
                      'border-[#CC0000] text-[#F59E0B]'
                    }`}>
                      <div className="text-center">
                        <p className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{score}</p>
                        <p className="text-[8px] font-mono uppercase tracking-wider opacity-70">/ 100</p>
                      </div>
                    </div>
                    <div>
                      <p className={`text-base font-bold ${score <= 30 ? 'text-green-400' : score <= 60 ? 'text-yellow-400' : 'text-[#F59E0B]'}`}>{verdict}</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {score <= 30 ? 'Great! Your resume sounds authentic and personal.' :
                         score <= 60 ? 'Some parts feel AI-generated. Recruiters may notice.' :
                         'High AI indicators detected. Recruiters will likely flag this.'}
                      </p>
                    </div>
                  </div>
                  {tells.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-mono font-bold text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>AI PATTERNS DETECTED</p>
                      <div className="space-y-1.5">{tells.map((t, i) => (<div key={i} className="flex items-start gap-2"><span className="text-[#F59E0B] flex-shrink-0 mt-0.5">!</span><p className="text-sm text-zinc-300">{t}</p></div>))}</div>
                    </div>
                  )}
                  {tips.length > 0 && (
                    <div>
                      <p className="text-xs font-mono font-bold text-green-400 mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>HOW TO MAKE IT MORE AUTHENTIC</p>
                      <div className="space-y-1.5">{tips.map((t, i) => (<div key={i} className="flex items-start gap-2"><span className="text-green-400 flex-shrink-0 mt-0.5">+</span><p className="text-sm text-zinc-300">{t}</p></div>))}</div>
                    </div>
                  )}
                    </>);
                  })()}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => { setAnalysis(null); setFile(null); setAudioSrc(null); setError(''); }}
                  className="text-sm px-6 py-3 border border-[#262626] text-zinc-300 hover:border-[#0077B6] hover:text-[#0077B6] transition-colors flex items-center gap-2"
                  data-testid="analyze-another-btn"
                >
                  <Upload className="w-4 h-4" /> Analyze Another Resume
                </button>
                <Link to="/courses" className="brand-btn text-sm px-6 py-3 inline-flex items-center gap-2">
                  View Our Courses <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <Link to="/jobs" className="text-sm px-6 py-3 border border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white transition-colors inline-flex items-center gap-2">
                  Browse Job Board <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
